import React, { useEffect, useState } from 'react';
import {
  get_sensor_list,
  sensor_configuration,
  update_sensor_status,
  update_delete_sensor,
  bp_units_data,
  add_sensor_type,
  get_sensor_type,
  get_machine_category,
} from '../../../utils/api';
import { toast } from 'react-toastify';
import {
  Table,
  Input,
  Switch,
  Select,
  Popconfirm,
  Tooltip,
  Modal,
  Button,
  Row,
  Col,
  Space,
  Form,
  Card,
  Tag,
  Typography,
  Divider,
} from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ConfigModal from './ConfigModal';
import { accessControlUtils } from '@/components/AccessControlUtils';
import ReusableSearchInput from '@/components/ReusableSearch';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
const { Title } = Typography;
function SensorView() {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  const [sensorData, setsensorData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formEdit, setFormEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [typeList, setTypeList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [sensorTypeInput, setSensorTypeInput] = useState('');
  const [getStatus, setGetStatus] = useState(false);
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Sensor Name is required'),
    category_id: Yup.string().required('Category is required'),
    unit_id: Yup.string().required('Unit is required'),
    make: Yup.string().required('Make is required'),
    quantity: Yup.number().required('Quantity is required'),
    poll_rate: Yup.number().required('Poll Rate is required'),
    sensor_status: Yup.string().required('Status is required'),
    is_read: Yup.boolean().nullable(),
    is_write: Yup.boolean().nullable(),
  });

  const handleAdd = (values, resetForm) => {
    const req = {
      name: values.name,
      category_id: values.category_id,
      unit_id: values.unit_id,
      make: values.make,
      sensor_status: values.sensor_status === 'Active',
      ip_address: values.ip_address || '',
      port: values.port || '',
      type: values.type_id || '',
      address: values.address || '',
      poll_rate: values.poll_rate || '',
      quantity: values.quantity || '',
      is_read: values.is_read || false,
      is_write: values.is_write || false,
    };

    if (formEdit) {
      update_delete_sensor({ ...req, update: true, id: editId }, successUpdateDelete);
    } else {
      sensor_configuration(req, successAdd);
    }
  };

  const successAdd = (res) => {
    if (res?.status === 'success') {
      toast.success(res?.message);
      get_all_sensor();
      handleCloseForm();
    } else {
      toast.warning(res?.message);
    }
  };

  const successUpdateDelete = (res) => {
    if (res?.status === 'success') {
      toast.success(res?.message);
      get_all_sensor();
      handleCloseForm();
    } else {
      toast.warning(res?.message);
    }
  };

  const handleEdit = (record) => {
    setFormEdit(true);
    setShowForm(true);
    setEditId(record?.sensor_id);
    // pass values to Formik via enableReinitialize
    setInitialValues({
      name: record.sensor_name,
      ip_address: record.ip_address,
      port: record.port,
      type_id: record.type_id,
      category_id: record.category_id,
      unit_id: record.unit_id,
      make: record.make,
      address: record.address,
      quantity: record.quantity,
      poll_rate: record.poll_rate,
      sensor_status: record.status ? 'Active' : 'InActive',
      is_read: record.is_read || false,
      is_write: record.is_write || false,
    });
  };

  const defaultFormValues = {
    name: '',
    ip_address: '',
    port: '',
    type_id: '',
    category_id: '',
    unit_id: '',
    make: '',
    address: '',
    quantity: '',
    poll_rate: '',
    sensor_status: '',
    is_read: true,
    is_write: false,
  };

  const [initialValues, setInitialValues] = useState(defaultFormValues);

  const handleCloseForm = () => {
    setShowForm(false);
    setFormEdit(false);
    setEditId(null);
    setInitialValues(defaultFormValues);
  };

  const onChangeStatus = (checked, record) => {
    update_sensor_status({ sensor_id: record.sensor_id, sensor_status: checked }, (res) => {
      if (res.status === 'success') {
        toast.success(res.message);
        get_all_sensor();
      } else toast.warning(res.message);
    });
  };

  const get_all_sensor = async () => {
    try {
      await get_sensor_list((res) => setsensorData(res?.data?.data || []));
    } catch (e) {
      console.error(e);
    }
  };

  const get_all_dropdown = async () => {
    try {
      await bp_units_data((res) => setUnitList(res?.data?.data || []));
      await get_sensor_type((res) => setTypeList(res?.data?.data || []));
      await get_machine_category((res) => setCategoryList(res?.data?.data || []));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    get_all_sensor();
    get_all_dropdown();
  }, []);

  const allColumns = [
    {
      title: 'Sensor Name',
      dataIndex: 'sensor_name',
      key: 'sensor_name',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Poll Rate',
      dataIndex: 'poll_rate',
      key: 'poll_rate',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'boolean',
      render: (val, rec) => (
        <Tooltip title={val ? 'InActive' : 'Active'}>
          <Switch
            checked={val}
            disabled={!accessControlUtils('Configuration')?.create_access}
            onChange={(checked) => onChangeStatus(checked, rec)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      fixed: 'right',
      align: 'center',
      defaultVisible: true,
      alwaysVisible: true,
      sortable: false,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Icons.Pencil
              onClick={() => handleEdit(record)}
              className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this sensor?"
            onConfirm={() => update_delete_sensor({ id: record.sensor_id }, successUpdateDelete)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'sensor_config',
    columns: allColumns,
    data: sensorData,
    searchValue: searchValue,
  });

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Activity className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {t('Sensor')}
            </Title>
          </div>
        }
        // style={{ backgroundColor: cardBgColor, borderColor: borderColor }}
        extra={
          <div className="flex items-center gap-2">
            {accessControlUtils('Configuration')?.create_access && (
              <Tooltip title={showForm ? 'Close Form' : 'Add Sensor'} placement="bottom">
                {showForm ? (
                  <Icons.X
                    className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer text-red-500"
                    onClick={handleCloseForm}
                  />
                ) : (
                  <Icons.Plus
                    className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer text-black"
                    onClick={() => setShowForm(true)}
                  />
                )}
              </Tooltip>
            )}
            <TableColumnConfig menu={columnConfigMenu} />
            <ReusableSearchInput
              placeholder="Search here..."
              width={250}
              onSearch={(val) => setSearchValue(val)}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        }
      >
        {showForm && (
          <div
            className="rounded-xl border mb-5"
            style={{
              backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f8faff',
              borderColor: formEdit ? '#f59e0b' : '#3b82f6',
              borderLeftWidth: 4,
              borderLeftStyle: 'solid',
              padding: '20px 24px 12px',
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div
                className="p-1.5 rounded-md"
                style={{
                  backgroundColor: formEdit
                    ? isDark
                      ? 'rgba(245,158,11,0.08)'
                      : '#fffbeb'
                    : isDark
                    ? 'rgba(59,130,246,0.08)'
                    : '#eff6ff',
                }}
              >
                {formEdit ? (
                  <Icons.Pencil className="w-4 h-4" style={{ color: '#f59e0b' }} />
                ) : (
                  <Icons.Plus className="w-4 h-4" style={{ color: '#3b82f6' }} />
                )}
              </div>
              <span
                className="text-sm font-semibold"
                style={{ color: formEdit ? '#f59e0b' : '#3b82f6' }}
              >
                {formEdit ? 'Edit Sensor' : 'Add New Sensor'}
              </span>
              {formEdit && (
                <Tag color="warning" className="ml-2 text-xs">
                  Editing
                </Tag>
              )}
            </div>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => handleAdd(values, resetForm)}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                setFieldValue,
                errors,
                touched,
                handleReset,
              }) => (
                <Form layout="vertical" onFinish={handleSubmit}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Sensor Name
                          </span>
                        }
                        validateStatus={touched.name && errors.name ? 'error' : ''}
                        help={touched.name && errors.name ? errors.name : ''}
                      >
                        <Input
                          size="large"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          placeholder="Enter sensor name"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            IP Address
                          </span>
                        }
                      >
                        <Input
                          size="large"
                          name="ip_address"
                          value={values.ip_address}
                          onChange={handleChange}
                          placeholder="Enter IP"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Port
                          </span>
                        }
                      >
                        <Input
                          size="large"
                          name="port"
                          value={values.port}
                          onChange={handleChange}
                          placeholder="Enter Port"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Type
                          </span>
                        }
                      >
                        <Space.Compact style={{ width: '100%' }}>
                          <Select
                            size="large"
                            allowClear
                            showSearch
                            placeholder="Select Type"
                            value={values.type_id}
                            onChange={(val) => setFieldValue('type_id', val)}
                            options={typeList.map((i) => ({
                              label: i.name,
                              value: i.id,
                            }))}
                            style={{ width: '100%' }}
                          />
                          <Tooltip title="Add Type">
                            <Button
                              icon={<Icons.Plus className="w-4 h-4" />}
                              onClick={() => setShowTypeModal(true)}
                            />
                          </Tooltip>
                        </Space.Compact>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Category
                          </span>
                        }
                        validateStatus={touched.category_id && errors.category_id ? 'error' : ''}
                        help={touched.category_id && errors.category_id ? errors.category_id : ''}
                      >
                        <Select
                          size="large"
                          allowClear
                          showSearch
                          placeholder="Select Category"
                          value={values.category_id}
                          onChange={(val) => setFieldValue('category_id', val)}
                          options={categoryList.map((i) => ({
                            label: i.category,
                            value: i.category_id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Make
                          </span>
                        }
                        validateStatus={touched.make && errors.make ? 'error' : ''}
                        help={touched.make && errors.make ? errors.make : ''}
                      >
                        <Input
                          size="large"
                          name="make"
                          value={values.make}
                          onChange={handleChange}
                          placeholder="Enter Manufacturer"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Address
                          </span>
                        }
                      >
                        <Input
                          size="large"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          placeholder="Enter Address"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Quantity
                          </span>
                        }
                        validateStatus={touched.quantity && errors.quantity ? 'error' : ''}
                        help={touched.quantity && errors.quantity ? errors.quantity : ''}
                      >
                        <Input
                          size="large"
                          name="quantity"
                          value={values.quantity}
                          onChange={handleChange}
                          placeholder="Enter Quantity"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Unit
                          </span>
                        }
                        validateStatus={touched.unit_id && errors.unit_id ? 'error' : ''}
                        help={touched.unit_id && errors.unit_id ? errors.unit_id : ''}
                      >
                        <Space.Compact style={{ width: '100%' }}>
                          <Select
                            size="large"
                            allowClear
                            showSearch
                            placeholder="Select Unit"
                            value={values.unit_id}
                            onChange={(val) => setFieldValue('unit_id', val)}
                            options={unitList.map((i) => ({
                              label: i.unit_name,
                              value: i.id,
                            }))}
                          />
                          <Tooltip title="Add Unit">
                            <Button
                              icon={<Icons.Plus className="w-4 h-4" />}
                              onClick={() => setShowModal(true)}
                            />
                          </Tooltip>
                        </Space.Compact>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Poll Rate
                          </span>
                        }
                        validateStatus={touched.poll_rate && errors.poll_rate ? 'error' : ''}
                        help={touched.poll_rate && errors.poll_rate ? errors.poll_rate : ''}
                      >
                        <Input
                          size="large"
                          name="poll_rate"
                          value={values.poll_rate}
                          onChange={handleChange}
                          placeholder="Enter Poll Rate"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Status
                          </span>
                        }
                        validateStatus={
                          touched.sensor_status && errors.sensor_status ? 'error' : ''
                        }
                        help={
                          touched.sensor_status && errors.sensor_status ? errors.sensor_status : ''
                        }
                      >
                        <Select
                          size="large"
                          allowClear
                          placeholder="Select Status"
                          value={values.sensor_status}
                          onChange={(val) => setFieldValue('sensor_status', val)}
                          options={[
                            { label: 'Active', value: 'Active' },
                            { label: 'InActive', value: 'InActive' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Read Access
                          </span>
                        }
                      >
                        <Switch
                          checked={values.is_read}
                          onChange={(checked) => setFieldValue('is_read', checked)}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="font-medium" style={{ color: textColor }}>
                            Write Access
                          </span>
                        }
                      >
                        <Switch
                          checked={values.is_write}
                          onChange={(checked) => setFieldValue('is_write', checked)}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24} className="flex justify-end gap-2 mt-2">
                      <Tooltip title="Reset">
                        <Button
                          icon={<Icons.Undo2 className="w-4 h-4" />}
                          onClick={() => {
                            handleReset();
                            handleCloseForm();
                          }}
                        />
                      </Tooltip>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={
                          formEdit ? (
                            <Icons.Check className="w-4 h-4" />
                          ) : (
                            <Icons.Plus className="w-4 h-4" />
                          )
                        }
                        style={
                          formEdit ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}
                        }
                      >
                        {formEdit ? 'Update' : 'Add'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </div>
        )}
        {showForm && <Divider className="my-2" />}
        {/* Outer bordered wrapper (not just the Table's own `bordered` cell-grid) —
            AntD's per-cell borders stop at the last data row, so when scroll.y's
            min-height reserves more space than the rows fill, that blank strip
            below the last row has no border on its own. Wrapping in a bordered,
            rounded, overflow-hidden div encloses the FULL reserved height instead,
            matching the PR list page (TicketList.js). */}
        <div style={{ border: `1px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden' }}>
          <Table
            className="sensor-config-table"
            dataSource={filteredData}
            columns={visibleColumns}
            rowKey="sensor_id"
            size="small"
            bordered
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
          />
        </div>
        {/* scroll.y only sets a max-height, not a floor — without this, the table
            shrinks to fit the empty-state placeholder instead of keeping its footprint.
            min-height here MUST match scroll.y exactly, matching the same convention
            used on every other converted table in the app (Product.js, MachineTypeConfig.js). */}
        <style>
          {`
            .sensor-config-table .ant-table-body {
              min-height: calc(100vh - 360px);
            }
            .sensor-config-table .ant-table-placeholder {
              height: calc(100vh - 360px);
            }
            .sensor-config-table .ant-table-placeholder > td {
              height: calc(100vh - 360px) !important;
              vertical-align: middle;
              border-bottom: none !important;
            }
          `}
        </style>
        <ConfigModal
          show_modal={showModal}
          set_show_modal={setShowModal}
          add_number={2}
          get_status={getStatus}
          set_get_status={setGetStatus}
        />
        <Modal
          open={showTypeModal}
          title="Add Sensor Type"
          onOk={() => {
            add_sensor_type({ name: sensorTypeInput }, () => {
              setShowTypeModal(false);
              setSensorTypeInput('');
              get_all_dropdown();
            });
          }}
          onCancel={() => setShowTypeModal(false)}
          okButtonProps={{ disabled: !sensorTypeInput }}
        >
          <Input
            size="large"
            placeholder="Enter Type"
            value={sensorTypeInput}
            onChange={(e) => setSensorTypeInput(e.target.value)}
          />
        </Modal>
      </Card>
    </div>
  );
}

export default SensorView;
